import type { Id } from "../_generated/dataModel";
import { parseEmailAddress, type EmailAddress, type EmailAddressParseError } from "./email";
import { ROLES, SUBSCRIPTION_TYPES, type CompanyStatus, type Role, type SubscriptionType } from "./enums";
import {
  prepareCompanyInsert,
  type CompanyInsert,
} from "./company";
import { err, ok, type Result } from "./result";
import { parseOptionalNonNegativeIntegerInput, type NonNegativeIntegerError } from "./numbers";
import { parseOneOf, type OneOfParseError } from "./guards";
import { cleanOptionalText } from "./text";
import { parseOptionalDateInputAtUtcHour } from "./time";
import { knownIdOrNullFromInput, type KnownIdOption } from "./idInput";

export { trialEndsAtFromDays } from "./company";

export type StaffCompanyDraft = {
  readonly name: string;
  readonly website: string;
  readonly logoUrl: string;
  readonly activePlanId: Id<"plans"> | null;
  readonly trialDays: string;
};

export type PreparedStaffCompanyCreate = {
  readonly name: string;
  readonly website?: string;
  readonly logoUrl?: string;
  readonly activePlanId: Id<"plans"> | null;
  readonly trialDays?: number;
};

export type StaffUserDraft = {
  readonly email: string;
  readonly name: string;
  readonly username: string;
  readonly role: Role;
  readonly accountExpiresOn: string;
};

export const DEFAULT_STAFF_COMPANY_TRIAL_DAYS = "14";

export const emptyStaffCompanyCreateFormState = (): StaffCompanyDraft => ({
  name: "",
  website: "",
  logoUrl: "",
  activePlanId: null,
  trialDays: DEFAULT_STAFF_COMPANY_TRIAL_DAYS,
});

export const emptyStaffUserCreateFormState = (): StaffUserDraft => ({
  email: "",
  name: "",
  username: "",
  role: "clientAdmin",
  accountExpiresOn: "",
});

export type StaffAdminSubmissionNotice =
  | { readonly kind: "success"; readonly text: string }
  | { readonly kind: "error"; readonly text: string };

export type StaffAdminSubmissionState = {
  readonly busy: boolean;
  readonly notice: StaffAdminSubmissionNotice | null;
};

export const emptyStaffAdminSubmissionState = (): StaffAdminSubmissionState => ({
  busy: false,
  notice: null,
});

export const staffAdminSuccessNotice = (text: string): StaffAdminSubmissionNotice => ({
  kind: "success",
  text,
});

export const staffAdminErrorNotice = (text: string): StaffAdminSubmissionNotice => ({
  kind: "error",
  text,
});

export type StaffAdminSubmissionAction =
  | { readonly type: "started" }
  | { readonly type: "succeeded"; readonly message: string }
  | { readonly type: "failed"; readonly error: string };

export const nextStaffAdminSubmissionState = (
  _current: StaffAdminSubmissionState,
  action: StaffAdminSubmissionAction,
): StaffAdminSubmissionState => {
  switch (action.type) {
    case "started":
      return { busy: true, notice: null };
    case "succeeded":
      return { busy: false, notice: staffAdminSuccessNotice(action.message) };
    case "failed":
      return { busy: false, notice: staffAdminErrorNotice(action.error) };
  }
};

export const staffAdminSubmissionNoticeToneClass = (notice: StaffAdminSubmissionNotice): string =>
  notice.kind === "success" ? "text-emerald-700" : "text-red-600";

export type StaffRoleOption = {
  readonly value: Role;
  readonly label: string;
};

export type StaffAdminUserCreateAccess = {
  readonly selectedCompany: boolean;
  readonly prepared: boolean;
};

const STAFF_ROLE_LABELS: Record<Role, string> = {
  clientAdmin: "Client Administrator",
  clientReadOnly: "Client Read Only",
  markupAdmin: "Markup Administrator",
  internalAdmin: "Internal Administrator",
};

export const staffRoleDisplayName = (role: Role): string => STAFF_ROLE_LABELS[role];

const STAFF_ROLE_OPTION_ORDER = [
  "clientAdmin",
  "clientReadOnly",
  "markupAdmin",
  "internalAdmin",
] as const satisfies readonly Role[];

export const STAFF_ROLE_OPTIONS: readonly StaffRoleOption[] = STAFF_ROLE_OPTION_ORDER.map((role) => ({
  value: role,
  label: staffRoleDisplayName(role),
}));

export const canCreateStaffUser = ({
  selectedCompany,
  prepared,
}: StaffAdminUserCreateAccess): boolean => selectedCompany && prepared;

export type StaffCompanyCreateFormView = {
  readonly prepared: Result<PreparedStaffCompanyCreate, StaffAdminFormError>;
  readonly touched: boolean;
  readonly problem: string | null;
  readonly canSubmit: boolean;
};

export type StaffUserCreateFormView = {
  readonly prepared: Result<PreparedStaffUserCreate, StaffAdminFormError>;
  readonly touched: boolean;
  readonly problem: string | null;
  readonly canSubmit: boolean;
};

export const staffCompanyCreateFormView = (
  draft: StaffCompanyDraft,
): StaffCompanyCreateFormView => {
  const prepared = prepareStaffCompanyCreate(draft);
  const touched = isStaffCompanyDraftTouched(draft);
  return {
    prepared,
    touched,
    problem: !prepared.ok && touched ? staffAdminFormErrorMessage(prepared.error) : null,
    canSubmit: prepared.ok,
  };
};

export const staffUserCreateFormView = ({
  draft,
  selectedCompany,
}: {
  readonly draft: StaffUserDraft;
  readonly selectedCompany: boolean;
}): StaffUserCreateFormView => {
  const prepared = prepareStaffUserCreate(draft);
  const touched = isStaffUserDraftTouched(draft);
  return {
    prepared,
    touched,
    problem: !prepared.ok && touched ? staffAdminFormErrorMessage(prepared.error) : null,
    canSubmit: canCreateStaffUser({ selectedCompany, prepared: prepared.ok }),
  };
};

export type StaffPlanOption<PlanId extends string = string> = KnownIdOption<PlanId>;

export const staffPlanIdFromInput = <PlanId extends string>(
  plans: readonly StaffPlanOption<PlanId>[],
  value: string,
  fallback: PlanId | null,
): PlanId | null => knownIdOrNullFromInput(plans, value, fallback);

export type StaffCompanyCreateFormAction =
  | { readonly type: "nameInput"; readonly value: string }
  | { readonly type: "websiteInput"; readonly value: string }
  | { readonly type: "logoUrlInput"; readonly value: string }
  | { readonly type: "activePlanInput"; readonly plans: readonly StaffPlanOption<Id<"plans">>[]; readonly value: string }
  | { readonly type: "trialDaysInput"; readonly value: string };

export const nextStaffCompanyCreateFormState = (
  current: StaffCompanyDraft,
  action: StaffCompanyCreateFormAction,
): StaffCompanyDraft => {
  switch (action.type) {
    case "nameInput":
      return { ...current, name: action.value };
    case "websiteInput":
      return { ...current, website: action.value };
    case "logoUrlInput":
      return { ...current, logoUrl: action.value };
    case "activePlanInput":
      return {
        ...current,
        activePlanId: staffPlanIdFromInput(action.plans, action.value, current.activePlanId),
      };
    case "trialDaysInput":
      return { ...current, trialDays: action.value };
  }
};

export const staffCompanyCreateFormSubmittedState = (
  current: StaffCompanyDraft,
): StaffCompanyDraft => ({
  ...current,
  name: "",
  website: "",
  logoUrl: "",
});

export type StaffUserCreateFormAction =
  | { readonly type: "emailInput"; readonly value: string }
  | { readonly type: "nameInput"; readonly value: string }
  | { readonly type: "usernameInput"; readonly value: string }
  | { readonly type: "roleInput"; readonly value: string }
  | { readonly type: "accountExpiresOnInput"; readonly value: string };

export const nextStaffUserCreateFormState = (
  current: StaffUserDraft,
  action: StaffUserCreateFormAction,
): StaffUserDraft => {
  switch (action.type) {
    case "emailInput":
      return { ...current, email: action.value };
    case "nameInput":
      return { ...current, name: action.value };
    case "usernameInput":
      return { ...current, username: action.value };
    case "roleInput":
      return { ...current, role: roleFromInput(action.value, current.role) };
    case "accountExpiresOnInput":
      return { ...current, accountExpiresOn: action.value };
  }
};

export const staffUserCreateFormSubmittedState = (
  current: StaffUserDraft,
): StaffUserDraft => ({
  ...current,
  email: "",
  name: "",
  username: "",
  accountExpiresOn: "",
});

export const staffAvailableCoverageLimit = (query: string): number =>
  query.trim() === "" ? 12 : 40;

export const isStaffCompanyDraftTouched = (draft: StaffCompanyDraft): boolean =>
  draft.name.trim() !== "" ||
  draft.website.trim() !== "" ||
  draft.logoUrl.trim() !== "" ||
  draft.activePlanId !== null ||
  draft.trialDays.trim() !== DEFAULT_STAFF_COMPANY_TRIAL_DAYS;

export const isStaffUserDraftTouched = (draft: StaffUserDraft): boolean =>
  draft.email.trim() !== "" ||
  draft.name.trim() !== "" ||
  draft.username.trim() !== "" ||
  draft.accountExpiresOn.trim() !== "";

export type PreparedStaffUserCreate = {
  readonly email: EmailAddress;
  readonly name?: string;
  readonly username?: string;
  readonly role: Role;
  readonly accountExpiresAt: number | null;
};

export type StaffCompanyCreateInput<PlanId extends string = string> = {
  readonly name: string;
  readonly website?: string;
  readonly logoUrl?: string;
  readonly activePlanId?: PlanId | null;
  readonly status?: CompanyStatus;
  readonly trialDays?: number;
};

export type PreparedStaffCompanyInsert<PlanId extends string = string> = CompanyInsert<PlanId, null>;

export type StaffCompanyUpdateSnapshot<PlanId extends string = string> = {
  readonly name: string;
  readonly website: string | null;
  readonly logoUrl: string | null;
  readonly activePlanId: PlanId | null;
  readonly status: CompanyStatus;
  readonly trialEndsAt: number | null;
};

export type StaffCompanyUpdateInput<PlanId extends string = string> = {
  readonly current: StaffCompanyUpdateSnapshot<PlanId>;
  readonly name?: string;
  readonly website?: string | null;
  readonly logoUrl?: string | null;
  readonly activePlanId?: PlanId | null;
  readonly status?: CompanyStatus;
  readonly trialEndsAt?: number | null;
};

export type StaffCompanyUpdatePatch<PlanId extends string = string> = StaffCompanyUpdateSnapshot<PlanId>;

export type StaffUserCreateInput<CompanyId extends string = string> = {
  readonly companyId: CompanyId;
  readonly email: string;
  readonly name?: string;
  readonly username?: string;
  readonly role: Role;
  readonly accountExpiresAt?: number | null;
};

export type PreparedStaffUserFields<CompanyId extends string = string> = {
  readonly companyId: CompanyId;
  readonly email: EmailAddress;
  readonly name: string | null;
  readonly username: string | null;
  readonly role: Role;
  readonly active: true;
  readonly accountExpiresAt: number | null;
  readonly passwordExpiresAt: number;
};

export type PreparedStaffUserInsert<CompanyId extends string = string> = PreparedStaffUserFields<CompanyId> & {
  readonly clerkUserId: string;
  readonly createdAt: number;
};

export type StaffAdminFormError =
  | { readonly kind: "missingCompanyName" }
  | { readonly kind: "invalidTrialDays" }
  | { readonly kind: "negativeTrialDays" }
  | { readonly kind: "missingUserEmail" }
  | { readonly kind: "invalidUserEmail" }
  | { readonly kind: "invalidUserExpiry" };

export const staffAdminFormErrorMessage = (error: StaffAdminFormError): string => {
  switch (error.kind) {
    case "missingCompanyName":
      return "Company name is required.";
    case "invalidTrialDays":
      return "Trial days must be a whole number.";
    case "negativeTrialDays":
      return "Trial days cannot be negative.";
    case "missingUserEmail":
      return "Email is required.";
    case "invalidUserEmail":
      return "Enter a valid email address.";
    case "invalidUserExpiry":
      return "Account expiry must be a valid date.";
  }
};


export const staffRoleInputResult = (value: string): Result<Role, OneOfParseError> =>
  parseOneOf(ROLES, value);

export const roleFromInput = (value: string, fallback: Role): Role => {
  const parsed = staffRoleInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

export const subscriptionTypeInputResult = (
  value: string,
): Result<SubscriptionType, OneOfParseError> =>
  parseOneOf(SUBSCRIPTION_TYPES, value);

export const subscriptionTypeFromInput = (
  value: string,
  fallback: SubscriptionType,
): SubscriptionType => {
  const parsed = subscriptionTypeInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

export const manualClerkUserId = (companyId: string, now: number): string =>
  `manual:${companyId}:${now.toString(36)}`;

export const prepareStaffCompanyInsert = <PlanId extends string>(
  input: StaffCompanyCreateInput<PlanId>,
  now: number,
): Result<PreparedStaffCompanyInsert<PlanId>, StaffAdminFormError> => {
  const prepared = prepareCompanyInsert({
    ...input,
    clerkOrgId: null,
    now,
  });
  return prepared.ok ? ok(prepared.value) : err(prepared.error);
};

export const prepareStaffCompanyUpdatePatch = <PlanId extends string>(
  input: StaffCompanyUpdateInput<PlanId>,
): StaffCompanyUpdatePatch<PlanId> => ({
  name: input.name ?? input.current.name,
  website: input.website === undefined ? input.current.website : input.website,
  logoUrl: input.logoUrl === undefined ? input.current.logoUrl : input.logoUrl,
  activePlanId: input.activePlanId === undefined ? input.current.activePlanId : input.activePlanId,
  status: input.status ?? input.current.status,
  trialEndsAt: input.trialEndsAt === undefined ? input.current.trialEndsAt : input.trialEndsAt,
});

export const prepareStaffUserFields = <CompanyId extends string>(
  input: StaffUserCreateInput<CompanyId>,
  now: number,
): Result<PreparedStaffUserFields<CompanyId>, StaffAdminFormError> => {
  const email = parseStaffEmail(input.email);
  if (!email.ok) return email;

  const accountExpiresAt = input.accountExpiresAt ?? null;
  if (accountExpiresAt !== null && !Number.isFinite(accountExpiresAt)) {
    return err({ kind: "invalidUserExpiry" });
  }

  return ok({
    companyId: input.companyId,
    email: email.value,
    name: cleanOptionalText(input.name ?? "") ?? null,
    username: cleanOptionalText(input.username ?? "") ?? null,
    role: input.role,
    active: true,
    accountExpiresAt,
    passwordExpiresAt: now,
  });
};

export const prepareStaffUserInsert = <CompanyId extends string>(
  fields: PreparedStaffUserFields<CompanyId>,
  now: number,
): PreparedStaffUserInsert<CompanyId> => ({
  clerkUserId: manualClerkUserId(fields.companyId, now),
  ...fields,
  createdAt: now,
});

export const prepareStaffCompanyCreate = (
  draft: StaffCompanyDraft,
): Result<PreparedStaffCompanyCreate, StaffAdminFormError> => {
  const name = draft.name.trim();
  if (name === "") return err({ kind: "missingCompanyName" });

  const trialDays = parseOptionalTrialDays(draft.trialDays);
  if (!trialDays.ok) return trialDays;

  const website = cleanOptionalText(draft.website);
  const logoUrl = cleanOptionalText(draft.logoUrl);

  return ok({
    name,
    ...(website === undefined ? {} : { website }),
    ...(logoUrl === undefined ? {} : { logoUrl }),
    activePlanId: draft.activePlanId,
    ...(trialDays.value === undefined ? {} : { trialDays: trialDays.value }),
  });
};

export const prepareStaffUserCreate = (
  draft: StaffUserDraft,
): Result<PreparedStaffUserCreate, StaffAdminFormError> => {
  const email = parseStaffEmail(draft.email);
  if (!email.ok) return email;

  const accountExpiresAt = parseOptionalDate(draft.accountExpiresOn);
  if (!accountExpiresAt.ok) return accountExpiresAt;

  const name = cleanOptionalText(draft.name);
  const username = cleanOptionalText(draft.username);

  return ok({
    email: email.value,
    ...(name === undefined ? {} : { name }),
    ...(username === undefined ? {} : { username }),
    role: draft.role,
    accountExpiresAt: accountExpiresAt.value,
  });
};

const parseStaffEmail = (value: string): Result<EmailAddress, StaffAdminFormError> => {
  const parsed = parseEmailAddress(value);
  if (parsed.ok) return ok(parsed.value);
  return err(staffEmailErrorFromEmailAddressError(parsed.error));
};

const staffEmailErrorFromEmailAddressError = (error: EmailAddressParseError): StaffAdminFormError => {
  switch (error.kind) {
    case "missingEmail":
      return { kind: "missingUserEmail" };
    case "invalidEmail":
      return { kind: "invalidUserEmail" };
  }
};

const parseOptionalTrialDays = (
  value: string,
): Result<number | undefined, StaffAdminFormError> => {
  const parsed = parseOptionalNonNegativeIntegerInput(value);
  return parsed.ok ? ok(parsed.value) : err(staffTrialDaysError(parsed.error));
};

const staffTrialDaysError = (error: NonNegativeIntegerError): StaffAdminFormError => {
  switch (error.kind) {
    case "invalidInteger":
      return { kind: "invalidTrialDays" };
    case "negativeInteger":
      return { kind: "negativeTrialDays" };
  }
};

const parseOptionalDate = (value: string): Result<number | null, StaffAdminFormError> => {
  const parsed = parseOptionalDateInputAtUtcHour(value, 0);
  return parsed.ok ? ok(parsed.value) : err({ kind: "invalidUserExpiry" });
};


